// B1/B2/E2 — quyết định hiển thị của hộp thoại "Thành viên nhóm" và "Cuộc trò chuyện mới": logic thuần để Main dùng
// và test E2E import trực tiếp (Node chạy được file .ts nhờ type stripping → chỉ dùng cú pháp TS xóa được).
import type { MemberRole, UserSummary } from '@swchat/protocol';

export const ROLE_LABEL: Record<MemberRole, string> = { owner: 'Chủ nhóm', admin: 'Quản trị', member: 'Thành viên' };

/** Câu hỏi xác nhận trước khi rời nhóm. */
export const LEAVE_CONFIRM = 'Rời nhóm này? Bạn sẽ không nhận tin mới nữa.';

export interface MemberRow {
  user_id: string;
  name: string;
  role: MemberRole;
  roleLabel: string;
  isMe: boolean;
  /** Nút đổi vai trò / xóa — chỉ có khi mình quản lý được (admin/owner), không phải mình, không phải owner. */
  actions?: {
    /** Vai trò mới khi bấm nút đổi vai trò (admin ↔ member) và nhãn nút. */
    toggleRole: { to: 'admin' | 'member'; label: string };
    /** Câu hỏi xác nhận trước khi xóa khỏi nhóm. */
    removeConfirm: string;
  };
}

/** Dòng danh sách thành viên: nhãn vai trò và nút quản lý theo quyền (`manage` = `client.canManage(conversationId)`). */
export function memberRows(
  members: readonly { user_id: string; role: MemberRole }[],
  me: string | undefined,
  manage: boolean,
  nameOf: (userId: string) => string,
): MemberRow[] {
  return members.map((m) => {
    const isMe = m.user_id === me;
    const name = nameOf(m.user_id);
    const row: MemberRow = { user_id: m.user_id, name, role: m.role, roleLabel: ROLE_LABEL[m.role], isMe };
    if (manage && !isMe && m.role !== 'owner') {
      row.actions = {
        toggleRole: m.role === 'admin' ? { to: 'member', label: 'Bỏ quản trị' } : { to: 'admin', label: 'Đặt quản trị' },
        removeConfirm: `Xóa ${name} khỏi nhóm?`,
      };
    }
    return row;
  });
}

/** Người có thể thêm vào nhóm: người thật, đã kích hoạt (có danh tính), chưa là thành viên. */
export function addCandidates<U extends UserSummary>(users: readonly U[], members: readonly { user_id: string }[]): U[] {
  const ids = new Set(members.map((m) => m.user_id));
  return users.filter((u) => u.kind === 'human' && u.has_identity && !ids.has(u.id));
}

/** Bot có thể thêm vào nhóm: đã được gateway kích hoạt (có danh tính), chưa là thành viên. */
export function botCandidates<U extends UserSummary>(users: readonly U[], members: readonly { user_id: string }[]): U[] {
  const ids = new Set(members.map((m) => m.user_id));
  return users.filter((u) => u.kind === 'bot' && u.has_identity && !ids.has(u.id));
}

/** Nút "Đổi tên" bật khi tên mới khác tên hiện tại và không rỗng. */
export function canRename(name: string, current: string): boolean {
  return !!name.trim() && name.trim() !== current;
}

/**
 * E2: danh sách người trong hộp thoại tạo cuộc trò chuyện — người thật trừ mình, lọc theo tên, thành viên dự án đang
 * chọn xếp trước (giữ thứ tự gốc trong từng phần).
 */
export function newChatUsers<U extends UserSummary>(
  users: readonly U[],
  me: string | undefined,
  filter: string,
  projectUsers: readonly string[],
): U[] {
  const inProject = new Set(projectUsers);
  const f = filter.toLowerCase();
  return users
    .filter((u) => u.id !== me && u.kind === 'human')
    .filter((u) => u.display_name.toLowerCase().includes(f))
    .sort((a, b) => Number(inProject.has(b.id)) - Number(inProject.has(a.id)));
}

/** E2: thành viên dự án được chọn sẵn khi gắn nhóm với dự án — trừ mình và người chưa kích hoạt (không mời vào MLS được). */
export function projectPreselect(projectUsers: readonly string[], users: readonly UserSummary[], me: string | undefined): string[] {
  const usable = new Set(users.filter((u) => u.has_identity).map((u) => u.id));
  return projectUsers.filter((id) => id !== me && usable.has(id));
}

/**
 * Đổi dự án trong hộp tạo nhóm (Q1#3): chỉ bỏ những người được chọn sẵn theo dự án trước (`prevAuto`) và thêm người
 * được chọn sẵn theo dự án mới (`nextAuto`); người đã tick tay (không thuộc `prevAuto`) được giữ nguyên.
 */
export function switchProjectPicks(picked: readonly string[], prevAuto: readonly string[], nextAuto: readonly string[]): string[] {
  const drop = new Set(prevAuto);
  const kept = picked.filter((id) => !drop.has(id));
  return [...kept, ...nextAuto.filter((id) => !kept.includes(id))];
}
